import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError, SideEventData, SideEventType, SideEventAppliesTo, SideEventConfigData, BracketConfigData, BracketEventData, SurvivorConfigData, EliminatorConfigData, SidePotConfigData, AdvancementType, SeedingMethod } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const SideEventsStep: React.FC<StepProps> = ({ data, updateData, errors, addError, clearErrors }) => {
  const { t } = useTranslation();
  const [newSideEvent, setNewSideEvent] = useState<SideEventData>({
    name: '',
    description: '',
    type: 'high_game',
    applies_to: 'bowler',
    applies_to_game_numbers: '',
    cost: 0,
    payout_formula: '',
    is_active: true
  });

  const [newSideEventConfig, setNewSideEventConfig] = useState<SideEventConfigData>({
    applies_to_series: false,
    scoring_criteria: '',
    custom_logic: null
  });

  // Separate states for BracketEvents and BracketConfigs
  const [newBracketEvent, setNewBracketEvent] = useState<BracketEventData>({
    name: '',
    applies_to: 'bowler',
    entry_cost: 0,
    payout_formula: '',
    bracket_size: 8,
    rounds: 3,
    is_active: true
  });

  const [newBracketConfig, setNewBracketConfig] = useState<BracketConfigData>({
    is_reversed: false,
    games_applied_to: '',
    custom_settings: {},
    advancement_type: 'higher_score',
    seeding_method: 'random'
  });

  const [newSurvivorConfig, setNewSurvivorConfig] = useState<SurvivorConfigData[]>([]);
  const [finalSurvivorBonus, setFinalSurvivorBonus] = useState<number>(0);

  const [newEliminatorConfig, setNewEliminatorConfig] = useState<EliminatorConfigData[]>([]);
  const [finalEliminatorBonus, setFinalEliminatorBonus] = useState<number>(0);

  const [newSidePotConfig, setNewSidePotConfig] = useState<SidePotConfigData[]>([]);
  const [availableGames, setAvailableGames] = useState<number[]>([]);
  const [selectedGamesForSeries, setSelectedGamesForSeries] = useState<number[]>([]);

  const [showConfig, setShowConfig] = useState(false);
  const [selectedEventIndex, setSelectedEventIndex] = useState<number | null>(null);

  // ── Eligibility filter state ──────────────────────────────────────────────
  const [femaleOnly, setFemaleOnly] = useState(false);
  const [seniorsOnly, setSeniorsOnly] = useState(false);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');

  // Generate survivor config based on applies_to_game_numbers
  const generateSurvivorConfig = (gameNumbers: string): SurvivorConfigData[] => {
    if (!gameNumbers) return [];

    const games = gameNumbers.split(',').map(num => parseInt(num.trim()));
    return games.map(gameNumber => ({
      game_number: gameNumber,
      survivor_score: 150, // Default survivor score
      round_payout: 0,
      final_payout: finalSurvivorBonus // Use the shared final bonus for all games
    }));
  };

  // Generate eliminator config based on applies_to_game_numbers
  const generateEliminatorConfig = (gameNumbers: string): EliminatorConfigData[] => {
    if (!gameNumbers) return [];

    const games = gameNumbers.split(',').map(num => parseInt(num.trim()));
    return games.map(gameNumber => ({
      game_number: gameNumber,
      advancement_percentage: 50, // Default 50% advance
      round_payout: 0,
      final_payout: finalEliminatorBonus // Use the shared final bonus for all games
    }));
  };

  // Generate side pot config based on applies_to_game_numbers (individual games only)
  const generateSidePotConfig = (gameNumbers: string): SidePotConfigData[] => {
    if (!gameNumbers.trim()) return [];

    const games = gameNumbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
    const configs: SidePotConfigData[] = [];

    // Add only individual game configurations
    games.forEach(gameNumber => {
      configs.push({
        config_type: 'individual_game',
        game_identifier: gameNumber.toString(),
        winners_count: 1, // Default to 1 winner
        payout_amount: 0
      });
    });

    return configs;
  };

  // Add a new series side pot configuration
  const addSeriesSidePot = () => {
    if (selectedGamesForSeries.length < 2) return;

    const gameIdentifier = selectedGamesForSeries.sort((a, b) => a - b).join('&');

    // Check if this series combination already exists
    const exists = newSidePotConfig.some(config =>
      config.config_type === 'game_series' && config.game_identifier === gameIdentifier
    );

    if (!exists) {
      const newSeriesConfig: SidePotConfigData = {
        config_type: 'game_series',
        game_identifier: gameIdentifier,
        winners_count: 1,
        payout_amount: 0
      };

      setNewSidePotConfig([...newSidePotConfig, newSeriesConfig]);
    }

    // Clear selection
    setSelectedGamesForSeries([]);
  };

  // Remove a series side pot configuration
  const removeSeriesSidePot = (gameIdentifier: string) => {
    setNewSidePotConfig(newSidePotConfig.filter(config =>
      !(config.config_type === 'game_series' && config.game_identifier === gameIdentifier)
    ));
  };

  // Toggle game selection for series creation
  const toggleGameForSeries = (gameNumber: number) => {
    if (selectedGamesForSeries.includes(gameNumber)) {
      setSelectedGamesForSeries(selectedGamesForSeries.filter(g => g !== gameNumber));
    } else {
      setSelectedGamesForSeries([...selectedGamesForSeries, gameNumber]);
    }
  };

  // Auto-manage applies_to_series based on event type
  useEffect(() => {
    if (newSideEvent.type === 'high_series' || newSideEvent.type === 'mystery_series') {
      // Auto-check applies_to_series for series events
      setNewSideEventConfig({
        ...newSideEventConfig,
        applies_to_series: true
      });
      // Clear game numbers since these events apply to series
      setNewSideEvent({ ...newSideEvent, applies_to_game_numbers: '' });
    } else if (newSideEvent.type === 'custom') {
      // All with All: always lock to doubles
      setNewSideEvent({ ...newSideEvent, applies_to: 'doubles' as SideEventAppliesTo });
    } else {
      // For all other event types (game-specific events), uncheck applies_to_series and disable checkbox
      setNewSideEventConfig({
        ...newSideEventConfig,
        applies_to_series: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSideEvent.type]);

  // Update survivor, eliminator, and side pot configs when game numbers change
  useEffect(() => {
    if (newSideEvent.type === 'survivor') {
      if (newSideEvent.applies_to_game_numbers && newSideEvent.applies_to_game_numbers.trim()) {
        const newConfig = generateSurvivorConfig(newSideEvent.applies_to_game_numbers);
        setNewSurvivorConfig(newConfig);
      } else {
        setNewSurvivorConfig([]);
      }
      setNewEliminatorConfig([]); // Clear eliminator when survivor is selected
      setNewSidePotConfig([]); // Clear side pot when survivor is selected
    } else if (newSideEvent.type === 'eliminator') {
      if (newSideEvent.applies_to_game_numbers && newSideEvent.applies_to_game_numbers.trim()) {
        const newConfig = generateEliminatorConfig(newSideEvent.applies_to_game_numbers);
        setNewEliminatorConfig(newConfig);
      } else {
        setNewEliminatorConfig([]);
      }
      setNewSurvivorConfig([]); // Clear survivor when eliminator is selected
      setNewSidePotConfig([]); // Clear side pot when eliminator is selected
    } else if (newSideEvent.type === 'side_pots' || newSideEvent.type === 'custom') {
      if (newSideEvent.applies_to_game_numbers && newSideEvent.applies_to_game_numbers.trim()) {
        const games = newSideEvent.applies_to_game_numbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
        setAvailableGames(games);
        const newConfig = generateSidePotConfig(newSideEvent.applies_to_game_numbers);
        setNewSidePotConfig(newConfig);
      } else {
        setAvailableGames([]);
        setNewSidePotConfig([]);
      }
      setSelectedGamesForSeries([]); // Clear series selection
      setNewSurvivorConfig([]); // Clear survivor when side pot is selected
      setNewEliminatorConfig([]); // Clear eliminator when side pot is selected
    } else {
      setNewSurvivorConfig([]);
      setNewEliminatorConfig([]);
      setNewSidePotConfig([]);
      setAvailableGames([]);
      setSelectedGamesForSeries([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSideEvent.type, newSideEvent.applies_to_game_numbers]);

  const handleAddSideEvent = () => {
    clearErrors();

    if (!newSideEvent.name || !newSideEvent.description) {
      addError('sideEvents', 'Side event name and description are required');
      return;
    }

    // Validate game numbers OR applies_to_series (but not both)
    const hasGameNumbers = newSideEvent.applies_to_game_numbers && newSideEvent.applies_to_game_numbers.trim();
    const appliesToSeries = newSideEventConfig.applies_to_series;

    if (!hasGameNumbers && !appliesToSeries) {
      addError('sideEvents', 'Either specify game numbers OR check "Applies to Series"');
      return;
    }

    if (hasGameNumbers && appliesToSeries) {
      addError('sideEvents', 'Cannot specify both game numbers and "Applies to Series" - choose one');
      return;
    }

    // Validate game numbers format if provided
    if (hasGameNumbers && newSideEvent.applies_to_game_numbers) {
      const gameNumbersRegex = /^[1-6](,[1-6])*$/;
      if (!gameNumbersRegex.test(newSideEvent.applies_to_game_numbers.trim())) {
        addError('sideEvents', 'Game numbers must be 1-6 separated by commas (e.g., "1,2,3")');
        return;
      }

      // Check for duplicate game numbers
      const gameNumbers = newSideEvent.applies_to_game_numbers.split(',').map(n => n.trim());
      const uniqueNumbers = Array.from(new Set(gameNumbers));
      if (gameNumbers.length !== uniqueNumbers.length) {
        addError('sideEvents', 'Duplicate game numbers are not allowed');
        return;
      }
    }

    if (newSideEvent.cost < 0) {
      addError('sideEvents', 'Cost cannot be negative');
      return;
    }

    const sideEvents = data.sideEvents || [];
    const eventToAdd: SideEventData = {
      ...newSideEvent,
      sideEventConfig: {
        ...(showConfig ? newSideEventConfig : { applies_to_series: false, scoring_criteria: '', custom_logic: null }),
        gender: femaleOnly ? 'F' : null,
        min_age: seniorsOnly ? 50 : (minAge ? parseInt(minAge) : null),
        max_age: maxAge ? parseInt(maxAge) : null,
      } as any,
      bracketEvent: newSideEvent.type === 'bracket' ? { ...newBracketEvent } : undefined,
      bracketConfig: newSideEvent.type === 'bracket' ? { ...newBracketConfig } : undefined,
      survivorConfig: newSideEvent.type === 'survivor' ? [...newSurvivorConfig] : undefined,
      eliminatorConfig: newSideEvent.type === 'eliminator' ? [...newEliminatorConfig] : undefined,
      sidePotConfig: (newSideEvent.type === 'side_pots' || newSideEvent.type === 'custom') ? [...newSidePotConfig] : undefined
    };

    updateData({
      sideEvents: [...sideEvents, eventToAdd]
    });

    // Reset form
    setNewSideEvent({
      name: '',
      description: '',
      type: 'high_game',
      applies_to: 'bowler',
      applies_to_game_numbers: '',
      cost: 0,
      payout_formula: '',
      is_active: true
    });

    setNewSideEventConfig({
      applies_to_series: false,
      scoring_criteria: '',
      custom_logic: null
    });

    setNewBracketEvent({
      name: '',
      applies_to: 'bowler',
      entry_cost: 0,
      payout_formula: '',
      bracket_size: 8,
      rounds: 3,
      is_active: true
    });

    setNewBracketConfig({
      is_reversed: false,
      games_applied_to: '',
      custom_settings: {},
      advancement_type: 'higher_score',
      seeding_method: 'random'
    });

    setNewSurvivorConfig([]);
    setFinalSurvivorBonus(0);
    setNewEliminatorConfig([]);
    setFinalEliminatorBonus(0);
    setNewSidePotConfig([]);
    setAvailableGames([]);
    setSelectedGamesForSeries([]);
    setFemaleOnly(false);
    setSeniorsOnly(false);
    setMinAge('');
    setMaxAge('');

    setShowConfig(false);
    clearErrors();
  };

  const handleRemoveSideEvent = (index: number) => {
    const sideEvents = data.sideEvents || [];
    const updatedEvents = sideEvents.filter((_, i) => i !== index);
    updateData({ sideEvents: updatedEvents });
    if (selectedEventIndex === index) {
      setSelectedEventIndex(null);
    }
  };

  const getFieldError = (field: string) => {
    return errors.find(e => e.field === field)?.message;
  };

  const sideEventTypes: { value: SideEventType; label: string; description: string }[] = [
    { value: 'high_game', label: t('admin.createLeague.steps.sideEventsStep.types.highGame.label'), description: t('admin.createLeague.steps.sideEventsStep.types.highGame.description') },
    { value: 'high_series', label: t('admin.createLeague.steps.sideEventsStep.types.highSeries.label'), description: t('admin.createLeague.steps.sideEventsStep.types.highSeries.description') },
    { value: 'mystery_score', label: t('admin.createLeague.steps.sideEventsStep.types.mysteryScore.label'), description: t('admin.createLeague.steps.sideEventsStep.types.mysteryScore.description') },
    { value: 'mystery_series', label: t('admin.createLeague.steps.sideEventsStep.types.mysterySeries.label'), description: t('admin.createLeague.steps.sideEventsStep.types.mysterySeries.description') },
    { value: 'side_pots', label: t('admin.createLeague.steps.sideEventsStep.types.sidePots.label'), description: t('admin.createLeague.steps.sideEventsStep.types.sidePots.description') },
    { value: 'survivor', label: t('admin.createLeague.steps.sideEventsStep.types.survivor.label'), description: t('admin.createLeague.steps.sideEventsStep.types.survivor.description') },
    { value: 'eliminator', label: t('admin.createLeague.steps.sideEventsStep.types.eliminator.label'), description: t('admin.createLeague.steps.sideEventsStep.types.eliminator.description') },
    { value: 'clean_game', label: t('admin.createLeague.steps.sideEventsStep.types.cleanGame.label'), description: t('admin.createLeague.steps.sideEventsStep.types.cleanGame.description') },
    { value: 'bracket', label: t('admin.createLeague.steps.sideEventsStep.types.bracket.label'), description: t('admin.createLeague.steps.sideEventsStep.types.bracket.description') },
    { value: 'custom', label: t('admin.createLeague.steps.sideEventsStep.types.custom.label'), description: t('admin.createLeague.steps.sideEventsStep.types.custom.description') }
  ];



  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.sideEventsStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.sideEventsStep.subtitle')}
        </p>
      </div>

      {/* Existing Side Events */}
      {data.sideEvents && data.sideEvents.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">{t('admin.createLeague.steps.sideEventsStep.currentEvents')}</h3>
          {data.sideEvents.map((event, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4 border border-white/20">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-white">{event.name}</h4>
                    <span className={`px-2 py-1 text-xs rounded ${event.type === 'bracket' ? 'bg-red-500/20 text-red-200' :
                        event.type === 'high_game' ? 'bg-blue-500/20 text-blue-200' :
                          event.type === 'high_series' ? 'bg-green-500/20 text-green-200' :
                            event.type === 'mystery_score' ? 'bg-yellow-500/20 text-yellow-200' :
                              event.type === 'mystery_series' ? 'bg-yellow-600/20 text-yellow-300' :
                                event.type === 'side_pots' ? 'bg-orange-500/20 text-orange-200' :
                                  event.type === 'survivor' ? 'bg-red-600/20 text-red-300' :
                                    event.type === 'eliminator' ? 'bg-pink-500/20 text-pink-200' :
                                      event.type === 'clean_game' ? 'bg-cyan-500/20 text-cyan-200' :
                                        'bg-purple-500/20 text-purple-200'
                      }`}>
                      {sideEventTypes.find(t => t.value === event.type)?.label}
                    </span>
                  </div>

                  <p className="text-purple-200 text-sm mb-2">{event.description}</p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-purple-300">
                    <div>{t('admin.createLeague.steps.sideEventsStep.cost')}: ${event.cost.toFixed(2)}</div>
                    <div>{t('admin.createLeague.steps.sideEventsStep.appliesTo')}: {event.applies_to}</div>
                    {event.applies_to_game_numbers && (
                      <div>{t('admin.createLeague.steps.sideEventsStep.games')}: {event.applies_to_game_numbers}</div>
                    )}
                    <div>{t('admin.createLeague.steps.sideEventsStep.status')}: {event.is_active ? t('admin.createLeague.steps.sideEventsStep.active') : t('admin.createLeague.steps.sideEventsStep.inactive')}</div>
                    {event.payout_formula && <div>{t('admin.createLeague.steps.sideEventsStep.payout')}: {event.payout_formula}</div>}
                  </div>

                  {/* Show bracket configuration if it's a bracket event */}
                  {event.type === 'bracket' && (event.bracketEvent || event.bracketConfig) && (
                    <div className="mt-3 p-3 bg-red-500/10 border border-red-400/30 rounded">
                      <h5 className="text-sm font-medium text-red-200 mb-2">{t('admin.createLeague.steps.sideEventsStep.bracket.configTitle')}</h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-red-300">
                        {event.bracketEvent && (
                          <>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.size')}: {event.bracketEvent.bracket_size} {t('admin.createLeague.steps.sideEventsStep.bracket.players')}</div>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.rounds')}: {event.bracketEvent.rounds}</div>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.entryCost')}: ${event.bracketEvent.entry_cost}</div>
                          </>
                        )}
                        {event.bracketConfig && (
                          <>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.advancement')}: {event.bracketConfig.advancement_type.replace('_', ' ')}</div>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.seeding')}: {event.bracketConfig.seeding_method.replace('_', ' ')}</div>
                            <div>{t('admin.createLeague.steps.sideEventsStep.bracket.reversed')}: {event.bracketConfig.is_reversed ? t('admin.createLeague.steps.sideEventsStep.yes') : t('admin.createLeague.steps.sideEventsStep.no')}</div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Show additional config if present */}
                  {event.sideEventConfig && (
                    <div className="mt-3 p-3 bg-blue-500/10 border border-blue-400/30 rounded">
                      <h5 className="text-sm font-medium text-blue-200 mb-2">{t('admin.createLeague.steps.sideEventsStep.eventConfig')}</h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-300">
                        {event.sideEventConfig.applies_to_series &&
                          <div>{t('admin.createLeague.steps.sideEventsStep.appliesToSeries')}: {t('admin.createLeague.steps.sideEventsStep.yes')}</div>}
                        {event.sideEventConfig.scoring_criteria &&
                          <div>{t('admin.createLeague.steps.sideEventsStep.scoringCriteria')}: {event.sideEventConfig.scoring_criteria}</div>}
                      </div>
                      {event.sideEventConfig.custom_logic && (
                        <div className="mt-2 text-xs text-blue-300">
                          <strong>{t('admin.createLeague.steps.sideEventsStep.customLogic')}:</strong> {JSON.stringify(event.sideEventConfig.custom_logic)}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleRemoveSideEvent(index)}
                  className="text-red-400 hover:text-red-300 ml-4"
                >
                  {t('admin.createLeague.steps.sideEventsStep.remove')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Side Event */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.sideEventsStep.addNew')}</h3>

        <div className="space-y-4">
          {/* Basic Event Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.sideEventsStep.eventName')} *
              </label>
              <input
                type="text"
                value={newSideEvent.name}
                onChange={(e) => setNewSideEvent({ ...newSideEvent, name: e.target.value })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder={t('admin.createLeague.steps.sideEventsStep.eventNamePlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.sideEventsStep.eventType')} *
              </label>
              <select
                value={newSideEvent.type}
                onChange={(e) => setNewSideEvent({ ...newSideEvent, type: e.target.value as SideEventType })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {sideEventTypes.map(type => (
                  <option key={type.value} value={type.value} className="bg-gray-800">
                    {type.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-purple-300 mt-1">
                {sideEventTypes.find(t => t.value === newSideEvent.type)?.description}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.sideEventsStep.description')} *
            </label>
            <textarea
              rows={3}
              value={newSideEvent.description}
              onChange={(e) => setNewSideEvent({ ...newSideEvent, description: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.sideEventsStep.descriptionPlaceholder')}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.sideEventsStep.entryCost')}
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={newSideEvent.cost}
                onChange={(e) => setNewSideEvent({ ...newSideEvent, cost: parseFloat(e.target.value) || 0 })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                {t('admin.createLeague.steps.sideEventsStep.appliesTo')}
              </label>
              <select
                value={newSideEvent.applies_to}
                onChange={(e) => setNewSideEvent({ ...newSideEvent, applies_to: e.target.value as SideEventAppliesTo })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={newSideEvent.type === 'custom'}
              >
                <option value="bowler" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.individualBowler')}</option>
                <option value="doubles" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.doubles')}</option>
                <option value="team" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.team')}</option>
              </select>
              {newSideEvent.type === 'custom' && (
                <p className="text-xs text-amber-300 mt-1">Locked to Doubles for All with All</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">
                <span className={`${!newSideEventConfig.applies_to_series ? 'text-red-400' : 'text-gray-400'}`}>
                  {!newSideEventConfig.applies_to_series ? '*' : ''}
                </span> {t('admin.createLeague.steps.sideEventsStep.gameNumbers')} {!newSideEventConfig.applies_to_series ? t('admin.createLeague.steps.sideEventsStep.gameNumbersRequired') : t('admin.createLeague.steps.sideEventsStep.gameNumbersDisabled')}
              </label>
              <input
                type="text"
                value={newSideEvent.applies_to_game_numbers || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow any input while typing, validate on blur/submit
                  setNewSideEvent({ ...newSideEvent, applies_to_game_numbers: value });
                }}
                onBlur={(e) => {
                  const value = e.target.value.trim();
                  if (value && !/^[1-6](,[1-6])*$/.test(value)) {
                    addError('sideEvents', 'Game numbers must be 1-6 separated by commas (e.g., "1,2,3")');
                  } else {
                    clearErrors();
                  }
                }}
                placeholder={newSideEventConfig.applies_to_series ? t('admin.createLeague.steps.sideEventsStep.gameNumbersPlaceholderSeries') : t('admin.createLeague.steps.sideEventsStep.gameNumbersPlaceholder')}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={newSideEventConfig.applies_to_series}
                required={!newSideEventConfig.applies_to_series}
              />
              <p className="text-xs text-purple-300 mt-1">
                {!newSideEventConfig.applies_to_series ? (
                  <><strong>{t('admin.createLeague.steps.sideEventsStep.required')}:</strong> {t('admin.createLeague.steps.sideEventsStep.gameNumbersHelp')}</>
                ) : (
                  <><strong>{t('admin.createLeague.steps.sideEventsStep.seriesEvent')}:</strong> {t('admin.createLeague.steps.sideEventsStep.seriesEventHelp')}</>
                )}
              </p>
            </div>
          </div>

          {/* Applies to Series Checkbox - Moved from Advanced Configuration */}
          <div className="flex items-center mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newSideEventConfig.applies_to_series || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setNewSideEventConfig({
                    ...newSideEventConfig,
                    applies_to_series: isChecked
                  });
                  // Clear game numbers when applies to series is checked
                  if (isChecked) {
                    setNewSideEvent({ ...newSideEvent, applies_to_game_numbers: '' });
                  }
                }}
                disabled={newSideEvent.type !== 'high_series' && newSideEvent.type !== 'mystery_series' && newSideEvent.type !== 'custom'}
                className="mr-2 h-4 w-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2 disabled:opacity-50"
              />
              <span className="text-purple-200 text-sm">
                {t('admin.createLeague.steps.sideEventsStep.appliesToSeries')}
                {(newSideEvent.type === 'high_series' || newSideEvent.type === 'mystery_series') &&
                  <span className="text-purple-300 ml-1">({t('admin.createLeague.steps.sideEventsStep.autoSelected')})</span>
                }
                {(newSideEvent.type !== 'high_series' && newSideEvent.type !== 'mystery_series' && newSideEvent.type !== 'custom') &&
                  <span className="text-gray-400 ml-1">({t('admin.createLeague.steps.sideEventsStep.onlyAvailableFor')})</span>
                }
              </span>
            </label>
          </div>

          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newSideEvent.is_active}
                onChange={(e) => setNewSideEvent({ ...newSideEvent, is_active: e.target.checked })}
                className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
              />
              <span className="ml-2 text-purple-200">{t('admin.createLeague.steps.sideEventsStep.eventActive')}</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-2">
              {t('admin.createLeague.steps.sideEventsStep.payoutFormula')}
            </label>
            <input
              type="text"
              value={newSideEvent.payout_formula || ''}
              onChange={(e) => setNewSideEvent({ ...newSideEvent, payout_formula: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('admin.createLeague.steps.sideEventsStep.payoutFormulaPlaceholder')}
            />
          </div>

          {/* Bracket Configuration - Only show if bracket type is selected */}
          {newSideEvent.type === 'bracket' && (
            <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-red-100 mb-4">{t('admin.createLeague.steps.sideEventsStep.bracket.title')}</h4>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-red-200 mb-2">
                    {t('admin.createLeague.steps.sideEventsStep.bracket.size')}
                  </label>
                  <select
                    value={newBracketEvent.bracket_size}
                    onChange={(e) => setNewBracketEvent({ ...newBracketEvent, bracket_size: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value={4} className="bg-gray-800">4 {t('admin.createLeague.steps.sideEventsStep.bracket.players')}</option>
                    <option value={8} className="bg-gray-800">8 {t('admin.createLeague.steps.sideEventsStep.bracket.players')}</option>
                    <option value={16} className="bg-gray-800">16 {t('admin.createLeague.steps.sideEventsStep.bracket.players')}</option>
                    <option value={32} className="bg-gray-800">32 {t('admin.createLeague.steps.sideEventsStep.bracket.players')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-red-200 mb-2">
                    {t('admin.createLeague.steps.sideEventsStep.bracket.numberOfRounds')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={newBracketEvent.rounds}
                    onChange={(e) => setNewBracketEvent({ ...newBracketEvent, rounds: parseInt(e.target.value) || 3 })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-red-200 mb-2">
                    {t('admin.createLeague.steps.sideEventsStep.bracket.entryCost')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={newBracketEvent.entry_cost}
                    onChange={(e) => setNewBracketEvent({ ...newBracketEvent, entry_cost: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-red-200 mb-2">
                    {t('admin.createLeague.steps.sideEventsStep.bracket.advancementType')}
                  </label>
                  <select
                    value={newBracketConfig.advancement_type}
                    onChange={(e) => setNewBracketConfig({ ...newBracketConfig, advancement_type: e.target.value as AdvancementType })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="higher_score" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.bracket.higherScoreHDCP')}</option>
                    <option value="total_pins" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.bracket.totalPins')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-red-200 mb-2">
                    {t('admin.createLeague.steps.sideEventsStep.bracket.seedingMethod')}
                  </label>
                  <select
                    value={newBracketConfig.seeding_method}
                    onChange={(e) => setNewBracketConfig({ ...newBracketConfig, seeding_method: e.target.value as SeedingMethod })}
                    className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="random" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.bracket.random')}</option>
                    <option value="average" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.bracket.averageBased')}</option>
                    <option value="signup_order" className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.bracket.signupOrder')}</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-6 mt-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newBracketConfig.is_reversed}
                    onChange={(e) => setNewBracketConfig({ ...newBracketConfig, is_reversed: e.target.checked })}
                    className="w-4 h-4 text-red-600 bg-white/10 border-white/20 rounded focus:ring-red-500 focus:ring-2"
                  />
                  <span className="ml-2 text-red-200">{t('admin.createLeague.steps.sideEventsStep.bracket.reverseBracket')}</span>
                </label>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-red-200 mb-2">
                  {t('admin.createLeague.steps.sideEventsStep.bracket.gamesAppliedTo')}
                </label>
                <input
                  type="text"
                  value={newBracketConfig.games_applied_to || ''}
                  onChange={(e) => setNewBracketConfig({ ...newBracketConfig, games_applied_to: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="1,2,3"
                />
              </div>
            </div>
          )}

          {/* Survivor Configuration - Show immediately when survivor type is selected */}
          {newSideEvent.type === 'survivor' && (
            <div className="bg-orange-500/10 border border-orange-400/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-orange-100 mb-4">{t('admin.createLeague.steps.sideEventsStep.survivor.title')}</h4>

              {newSurvivorConfig.length === 0 ? (
                <div className="text-orange-200 text-sm p-4 bg-orange-500/20 rounded">
                  {t('admin.createLeague.steps.sideEventsStep.survivor.specifyGames')}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-orange-200 text-sm mb-4">
                    {t('admin.createLeague.steps.sideEventsStep.survivor.configureHelp')}
                  </div>

                  {newSurvivorConfig.map((config, index) => (
                    <div key={config.game_number} className="bg-orange-500/5 border border-orange-400/20 rounded-lg p-4">
                      <h5 className="text-orange-200 font-medium mb-3">{t('admin.createLeague.steps.sideEventsStep.survivor.gameConfig', { gameNumber: config.game_number })}</h5>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-orange-200 mb-2">
                            {t('admin.createLeague.steps.sideEventsStep.survivor.survivorScore')} *
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="1200"
                            value={config.survivor_score}
                            onChange={(e) => {
                              const newConfigs = [...newSurvivorConfig];
                              newConfigs[index].survivor_score = parseInt(e.target.value) || 150;
                              setNewSurvivorConfig(newConfigs);
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="150"
                          />
                          <p className="text-xs text-orange-300 mt-1">{t('admin.createLeague.steps.sideEventsStep.survivor.survivorScoreHelp')}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-orange-200 mb-2">
                            {t('admin.createLeague.steps.sideEventsStep.survivor.roundPayout')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={config.round_payout}
                            onChange={(e) => {
                              const newConfigs = [...newSurvivorConfig];
                              newConfigs[index].round_payout = parseFloat(e.target.value) || 0;
                              setNewSurvivorConfig(newConfigs);
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            placeholder="0.00"
                          />
                          <p className="text-xs text-orange-300 mt-1">{t('admin.createLeague.steps.sideEventsStep.survivor.roundPayoutHelp')}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Final Survivor Bonus - Single configuration for surviving all rounds */}
                  <div className="bg-orange-600/10 border border-orange-500/30 rounded-lg p-4 mt-6">
                    <h5 className="text-orange-100 font-medium mb-3">{t('admin.createLeague.steps.sideEventsStep.survivor.finalBonus')}</h5>
                    <div className="max-w-md">
                      <label className="block text-sm font-medium text-orange-200 mb-2">
                        {t('admin.createLeague.steps.sideEventsStep.survivor.finalPayout')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={finalSurvivorBonus}
                        onChange={(e) => {
                          const bonus = parseFloat(e.target.value) || 0;
                          setFinalSurvivorBonus(bonus);
                          // Update ONLY the final_payout field, preserving other fields
                          setNewSurvivorConfig(prevConfigs => 
                            prevConfigs.map(config => ({
                              ...config,
                              final_payout: bonus
                            }))
                          );
                        }}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-orange-300 mt-1">
                        {t('admin.createLeague.steps.sideEventsStep.survivor.finalPayoutHelp', { rounds: newSurvivorConfig.length })}
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-400/30 rounded p-3">
                    <p className="text-yellow-200 text-sm">
                      <strong>{t('admin.createLeague.steps.sideEventsStep.note')}:</strong> {t('admin.createLeague.steps.sideEventsStep.survivor.payoutNote')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Eliminator Configuration - Show immediately when eliminator type is selected */}
          {newSideEvent.type === 'eliminator' && (
            <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-red-100 mb-4">{t('admin.createLeague.steps.sideEventsStep.eliminator.title')}</h4>

              {newEliminatorConfig.length === 0 ? (
                <div className="text-red-200 text-sm p-4 bg-red-500/20 rounded">
                  {t('admin.createLeague.steps.sideEventsStep.eliminator.specifyGames')}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-red-200 text-sm mb-4">
                    {t('admin.createLeague.steps.sideEventsStep.eliminator.configureHelp')}
                  </div>

                  {newEliminatorConfig.map((config, index) => (
                    <div key={config.game_number} className="bg-red-500/5 border border-red-400/20 rounded-lg p-4">
                      <h5 className="text-red-200 font-medium mb-3">{t('admin.createLeague.steps.sideEventsStep.eliminator.gameConfig', { gameNumber: config.game_number })}</h5>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-red-200 mb-2">
                            {t('admin.createLeague.steps.sideEventsStep.eliminator.advancementPercentage')} *
                          </label>
                          <select
                            value={config.advancement_percentage}
                            onChange={(e) => {
                              const newConfigs = [...newEliminatorConfig];
                              newConfigs[index].advancement_percentage = parseInt(e.target.value);
                              setNewEliminatorConfig(newConfigs);
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <option value={75} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent75')}</option>
                            <option value={67} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent67')}</option>
                            <option value={60} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent60')}</option>
                            <option value={50} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent50')}</option>
                            <option value={40} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent40')}</option>
                            <option value={33} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent33')}</option>
                            <option value={25} className="bg-gray-800">{t('admin.createLeague.steps.sideEventsStep.eliminator.percent25')}</option>
                          </select>
                          <p className="text-xs text-red-300 mt-1">{t('admin.createLeague.steps.sideEventsStep.eliminator.advancementHelp')}</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-red-200 mb-2">
                            {t('admin.createLeague.steps.sideEventsStep.eliminator.roundPayout')}
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={config.round_payout}
                            onChange={(e) => {
                              const newConfigs = [...newEliminatorConfig];
                              newConfigs[index].round_payout = parseFloat(e.target.value) || 0;
                              setNewEliminatorConfig(newConfigs);
                            }}
                            className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="0.00"
                          />
                          <p className="text-xs text-red-300 mt-1">{t('admin.createLeague.steps.sideEventsStep.eliminator.roundPayoutHelp')}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Final Eliminator Bonus - Single configuration for surviving all rounds */}
                  <div className="bg-red-600/10 border border-red-500/30 rounded-lg p-4 mt-6">
                    <h5 className="text-red-100 font-medium mb-3">{t('admin.createLeague.steps.sideEventsStep.eliminator.finalPayout')}</h5>
                    <div className="max-w-md">
                      <label className="block text-sm font-medium text-red-200 mb-2">
                        {t('admin.createLeague.steps.sideEventsStep.eliminator.finalPayoutLabel')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={finalEliminatorBonus}
                        onChange={(e) => {
                          const bonus = parseFloat(e.target.value) || 0;
                          setFinalEliminatorBonus(bonus);
                          // Update ONLY the final_payout field, preserving other fields
                          setNewEliminatorConfig(prevConfigs => 
                            prevConfigs.map(config => ({
                              ...config,
                              final_payout: bonus
                            }))
                          );
                        }}
                        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-red-300 mt-1">
                        {t('admin.createLeague.steps.sideEventsStep.eliminator.finalPayoutHelp', { rounds: newEliminatorConfig.length })}
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-400/30 rounded p-3">
                    <p className="text-yellow-200 text-sm">
                      <strong>{t('admin.createLeague.steps.sideEventsStep.note')}:</strong> {t('admin.createLeague.steps.sideEventsStep.eliminator.eliminationNote')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Side Pot Configuration - Show immediately when side_pots or custom type is selected */}
          {(newSideEvent.type === 'side_pots' || newSideEvent.type === 'custom') && (
            <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-green-100 mb-4">{t('admin.createLeague.steps.sideEventsStep.sidePot.title')}</h4>

              {availableGames.length === 0 ? (
                <div className="text-green-200 text-sm p-4 bg-green-500/20 rounded">
                  {t('admin.createLeague.steps.sideEventsStep.sidePot.specifyGames')}
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-green-200 text-sm mb-4">
                    {t('admin.createLeague.steps.sideEventsStep.sidePot.configureHelp')}
                  </div>

                  {/* Individual Game Side Pots */}
                  <div className="space-y-3">
                    <h5 className="text-green-200 font-medium border-b border-green-400/30 pb-2">{t('admin.createLeague.steps.sideEventsStep.sidePot.individualTitle')}</h5>
                    {newSidePotConfig
                      .filter(config => config.config_type === 'individual_game')
                      .map((config, index) => (
                        <div key={`individual-${config.game_identifier}`} className="bg-green-500/5 border border-green-400/20 rounded-lg p-4">
                          <h6 className="text-green-200 font-medium mb-3">{t('admin.createLeague.steps.sideEventsStep.sidePot.gameSidePot', { gameNumber: config.game_identifier })}</h6>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-green-200 mb-2">
                                {t('admin.createLeague.steps.sideEventsStep.sidePot.numberOfWinners')} *
                              </label>
                              <select
                                value={config.winners_count}
                                onChange={(e) => {
                                  const newConfigs = [...newSidePotConfig];
                                  const configIndex = newConfigs.findIndex(c =>
                                    c.config_type === 'individual_game' && c.game_identifier === config.game_identifier
                                  );
                                  if (configIndex !== -1) {
                                    newConfigs[configIndex].winners_count = parseInt(e.target.value);
                                    setNewSidePotConfig(newConfigs);
                                  }
                                }}
                                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                              >
                                <option value={1} className="bg-gray-800">1 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowler')}</option>
                                <option value={2} className="bg-gray-800">2 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={3} className="bg-gray-800">3 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={4} className="bg-gray-800">4 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={5} className="bg-gray-800">5 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={6} className="bg-gray-800">6 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={7} className="bg-gray-800">7 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={8} className="bg-gray-800">8 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={9} className="bg-gray-800">9 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                <option value={10} className="bg-gray-800">10 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-green-200 mb-2">
                                {t('admin.createLeague.steps.sideEventsStep.sidePot.payoutAmount')}
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={config.payout_amount}
                                onChange={(e) => {
                                  const newConfigs = [...newSidePotConfig];
                                  const configIndex = newConfigs.findIndex(c =>
                                    c.config_type === 'individual_game' && c.game_identifier === config.game_identifier
                                  );
                                  if (configIndex !== -1) {
                                    newConfigs[configIndex].payout_amount = parseFloat(e.target.value) || 0;
                                    setNewSidePotConfig(newConfigs);
                                  }
                                }}
                                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Create Game Series Side Pots */}
                  {availableGames.length > 1 && (
                    <div className="space-y-4">
                      <h5 className="text-green-200 font-medium border-b border-green-400/30 pb-2">{t('admin.createLeague.steps.sideEventsStep.sidePot.createSeriesTitle')}</h5>
                      <div className="text-green-200 text-xs mb-3">
                        {t('admin.createLeague.steps.sideEventsStep.sidePot.selectGamesHelp')}
                      </div>

                      {/* Game Selection Interface */}
                      <div className="bg-green-500/5 border border-green-400/20 rounded-lg p-4">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-green-200 mb-3">
                            {t('admin.createLeague.steps.sideEventsStep.sidePot.selectGamesLabel')}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {availableGames.map(gameNumber => (
                              <button
                                key={gameNumber}
                                type="button"
                                onClick={() => toggleGameForSeries(gameNumber)}
                                className={`px-4 py-2 rounded-lg border transition-all ${selectedGamesForSeries.includes(gameNumber)
                                    ? 'bg-green-500 border-green-400 text-white shadow-lg'
                                    : 'bg-white/10 border-white/20 text-green-200 hover:bg-white/20'
                                  }`}
                              >
                                {t('admin.createLeague.steps.sideEventsStep.sidePot.gameButton', { gameNumber })}
                              </button>
                            ))}
                          </div>
                        </div>

                        {selectedGamesForSeries.length > 1 && (
                          <div className="flex items-center gap-4">
                            <div className="text-green-200 text-sm">
                              {t('admin.createLeague.steps.sideEventsStep.sidePot.selectedGames', { games: selectedGamesForSeries.sort((a, b) => a - b).join(' + ') })}
                            </div>
                            <button
                              type="button"
                              onClick={addSeriesSidePot}
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                              {t('admin.createLeague.steps.sideEventsStep.sidePot.addSeriesButton')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Existing Game Series Side Pots */}
                  {newSidePotConfig.some(config => config.config_type === 'game_series') && (
                    <div className="space-y-3">
                      <h5 className="text-green-200 font-medium border-b border-green-400/30 pb-2">{t('admin.createLeague.steps.sideEventsStep.sidePot.gameSeriesTitle')}</h5>
                      {newSidePotConfig
                        .filter(config => config.config_type === 'game_series')
                        .map((config, index) => (
                          <div key={`series-${config.game_identifier}`} className="bg-green-500/5 border border-green-400/20 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h6 className="text-green-200 font-medium">{t('admin.createLeague.steps.sideEventsStep.sidePot.combinedSeries', { games: config.game_identifier.replace(/&/g, ' + ') })}</h6>
                              <button
                                type="button"
                                onClick={() => removeSeriesSidePot(config.game_identifier)}
                                className="text-red-400 hover:text-red-300 text-sm"
                                title={t('admin.createLeague.steps.sideEventsStep.sidePot.removeSeriesTitle')}
                              >
                                {t('admin.createLeague.steps.sideEventsStep.sidePot.removeButton')}
                              </button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-green-200 mb-2">
                                  {t('admin.createLeague.steps.sideEventsStep.sidePot.numberOfWinners')} *
                                </label>
                                <select
                                  value={config.winners_count}
                                  onChange={(e) => {
                                    const newConfigs = [...newSidePotConfig];
                                    const configIndex = newConfigs.findIndex(c =>
                                      c.config_type === 'game_series' && c.game_identifier === config.game_identifier
                                    );
                                    if (configIndex !== -1) {
                                      newConfigs[configIndex].winners_count = parseInt(e.target.value);
                                      setNewSidePotConfig(newConfigs);
                                    }
                                  }}
                                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                  <option value={1} className="bg-gray-800">1 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowler')}</option>
                                  <option value={2} className="bg-gray-800">2 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={3} className="bg-gray-800">3 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={4} className="bg-gray-800">4 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={5} className="bg-gray-800">5 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={6} className="bg-gray-800">6 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={7} className="bg-gray-800">7 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={8} className="bg-gray-800">8 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={9} className="bg-gray-800">9 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                  <option value={10} className="bg-gray-800">10 {t('admin.createLeague.steps.sideEventsStep.sidePot.bowlers')}</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-green-200 mb-2">
                                  {t('admin.createLeague.steps.sideEventsStep.sidePot.payoutAmount')}
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={config.payout_amount}
                                  onChange={(e) => {
                                    const newConfigs = [...newSidePotConfig];
                                    const configIndex = newConfigs.findIndex(c =>
                                      c.config_type === 'game_series' && c.game_identifier === config.game_identifier
                                    );
                                    if (configIndex !== -1) {
                                      newConfigs[configIndex].payout_amount = parseFloat(e.target.value) || 0;
                                      setNewSidePotConfig(newConfigs);
                                    }
                                  }}
                                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  <div className="bg-yellow-500/10 border border-yellow-400/30 rounded p-3">
                    <p className="text-yellow-200 text-sm">
                      <strong>{t('admin.createLeague.steps.sideEventsStep.sidePot.proTip')}:</strong> {t('admin.createLeague.steps.sideEventsStep.sidePot.proTipText')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Advanced Configuration Toggle */}
          <div className="border-t border-white/10 pt-4">
            <button
              type="button"
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center gap-2 text-purple-200 hover:text-white transition-colors"
            >
              <span className={`transform transition-transform ${showConfig ? 'rotate-90' : ''}`}>▶</span>
              {t('admin.createLeague.steps.sideEventsStep.advancedConfig')}
            </button>
          </div>

          {/* Advanced Configuration */}
          {showConfig && (
            <div className="bg-blue-500/10 border border-blue-400/30 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-blue-100 mb-4">{t('admin.createLeague.steps.sideEventsStep.eventConfig')}</h4>



              <div className="mt-4">
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  {t('admin.createLeague.steps.sideEventsStep.scoringCriteria')}
                </label>
                <textarea
                  rows={3}
                  value={newSideEventConfig.scoring_criteria || ''}
                  onChange={(e) => setNewSideEventConfig({ ...newSideEventConfig, scoring_criteria: e.target.value })}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.createLeague.steps.sideEventsStep.scoringCriteriaPlaceholder')}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  {t('admin.createLeague.steps.sideEventsStep.customLogic')}
                </label>
                <textarea
                  rows={3}
                  value={newSideEventConfig.custom_logic ? JSON.stringify(newSideEventConfig.custom_logic, null, 2) : ''}
                  onChange={(e) => {
                    try {
                      const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                      setNewSideEventConfig({ ...newSideEventConfig, custom_logic: parsed });
                    } catch {
                      // Invalid JSON, keep the text as is for user to fix
                      setNewSideEventConfig({ ...newSideEventConfig, custom_logic: e.target.value });
                    }
                  }}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('admin.createLeague.steps.sideEventsStep.customLogicPlaceholder')}
                />
              </div>
            </div>
          )}

          {/* ── Eligibility Filters (Optional) ─────────────────────────────── */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-4 space-y-3">
            <p className="text-sm font-semibold text-purple-200">
              Eligibility Filters <span className="text-gray-500 font-normal text-xs">(Optional)</span>
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-purple-200">
              <input type="checkbox" checked={femaleOnly} onChange={e => setFemaleOnly(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500" />
              Female only event?
            </label>
            <label className={`flex items-center gap-2 text-sm ${(minAge || maxAge) ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-purple-200 cursor-pointer'}`}>
              <input type="checkbox" checked={seniorsOnly} disabled={!!(minAge || maxAge)}
                onChange={e => setSeniorsOnly(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 disabled:cursor-not-allowed" />
              Seniors event?
              <span className="text-xs text-gray-400">(sets min age to 50)</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-purple-300 mb-1">
                  Min Age <span className="text-gray-600">(Optional)</span>
                </label>
                <input type="number" min={1} max={100} value={minAge} placeholder="e.g. 50"
                  onChange={e => { setMinAge(e.target.value); if (e.target.value) setSeniorsOnly(false); }}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-purple-300 mb-1">
                  Max Age <span className="text-gray-600">(Optional)</span>
                </label>
                <input type="number" min={1} max={100} value={maxAge} placeholder="e.g. 65"
                  onChange={e => { setMaxAge(e.target.value); if (e.target.value) setSeniorsOnly(false); }}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500" />
              </div>
            </div>
          </div>

          <button
            onClick={handleAddSideEvent}
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200"
          >
            {t('admin.createLeague.steps.sideEventsStep.addButton')}
          </button>

          {getFieldError('sideEvents') && (
            <p className="text-red-400 text-sm mt-2">{getFieldError('sideEvents')}</p>
          )}
        </div>
      </div>

      {/* Examples */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.sideEventsStep.examples.title')}</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-purple-100 mb-3">{t('admin.createLeague.steps.sideEventsStep.examples.weeklyTitle')}</h4>
            <div className="space-y-2 text-purple-200 text-sm">
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.highGame')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.highGameDesc')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.mysteryScore')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.mysteryScoreDesc')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.strikeJackpot')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.strikeJackpotDesc')}</div>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-purple-100 mb-3">{t('admin.createLeague.steps.sideEventsStep.examples.tournamentTitle')}</h4>
            <div className="space-y-2 text-purple-200 text-sm">
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.bracket')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.bracketDesc')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.highSeries')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.highSeriesDesc')}</div>
              </div>
              <div className="p-2 bg-white/5 rounded">
                <div className="font-medium">{t('admin.createLeague.steps.sideEventsStep.examples.teamChampionship')}</div>
                <div className="text-xs">{t('admin.createLeague.steps.sideEventsStep.examples.teamChampionshipDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(!data.sideEvents || data.sideEvents.length === 0) && (
        <div className="bg-yellow-500/20 border border-yellow-400 rounded-lg p-4 text-center">
          <p className="text-yellow-200">
            {t('admin.createLeague.steps.sideEventsStep.noEventsWarning')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SideEventsStep;